import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import api from '@/lib/api';
import Avatar from '@/components/Avatar';
import Preloader from '@/components/Preloader';
import FormularioUsuario from './FormularioUsuario';
import { resumoDoAcesso, type RespostaUsuarios, type Usuario } from './tipos';

/**
 * Quem usa o sistema da empresa e o que cada um alcança. Quem sai é inativado,
 * não excluído: o nome continua no histórico (U-06).
 */
export default function UsuariosPage() {
  const queryClient = useQueryClient();
  // undefined: fechado; null: usuário novo.
  const [editando, setEditando] = useState<Usuario | null | undefined>(undefined);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['usuarios'],
    queryFn: async () => (await api.get<RespostaUsuarios>('/usuarios')).data,
  });

  if (isLoading) {
    return <Preloader telaCheia />;
  }

  if (isError || !data) {
    return <p className="px-lg py-lg text-[14px] text-danger">Não foi possível carregar os usuários.</p>;
  }

  const inativos = data.data.filter((usuario) => !usuario.ativo).length;
  const visiveis = data.data.filter((usuario) => mostrarInativos || usuario.ativo);

  return (
    <div className="max-w-[1100px] px-lg py-lg">
      <header className="mb-lg flex flex-wrap items-end justify-between gap-md">
        <div>
          <h1 className="text-[22px] font-bold text-ink">Usuários</h1>
          <p className="mt-xs text-[13px] text-muted">
            Quem usa o sistema da empresa e o que cada um alcança. Quem sai é inativado: o nome continua no histórico.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditando(null)}
          className="flex h-10 items-center gap-sm rounded-lg bg-accent px-md text-[14px] font-bold text-white hover:bg-accent/90"
        >
          <Plus size={16} />
          Novo usuário
        </button>
      </header>

      {inativos > 0 && (
        <label className="mb-sm flex w-fit cursor-pointer items-center gap-xs text-[13px] text-muted">
          <input type="checkbox" checked={mostrarInativos} onChange={(evento) => setMostrarInativos(evento.target.checked)} className="accent-accent" />
          Mostrar inativos ({inativos})
        </label>
      )}

      <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface">
        {visiveis.map((usuario) => (
          <li key={usuario.id} className={`flex flex-wrap items-center gap-md px-md py-sm ${usuario.ativo ? '' : 'opacity-60'}`}>
            <Avatar nome={usuario.nome} fotoUrl={usuario.foto_url} tamanho="md" />
            <div className="min-w-[220px] flex-1">
              <p className="flex flex-wrap items-center gap-xs text-[14px] font-bold text-ink">
                {usuario.nome}
                {usuario.eu && <span className="rounded-full bg-surface-subtle px-sm text-[11px] font-bold text-muted">você</span>}
                {!usuario.ativo && <span className="rounded-full bg-danger/10 px-sm text-[11px] font-bold text-danger">inativo</span>}
                {usuario.ativo && usuario.trocar_senha && (
                  <span className="rounded-full bg-warning/15 px-sm text-[11px] font-bold text-warning">senha provisória</span>
                )}
              </p>
              <p className="text-[13px] text-muted">{usuario.email}</p>
            </div>
            <p className={`min-w-[200px] flex-1 text-[13px] ${usuario.administrador ? 'font-bold text-accent' : 'text-ink'}`}>
              {resumoDoAcesso(usuario, data.permissoes)}
            </p>
            <button
              type="button"
              onClick={() => setEditando(usuario)}
              title={usuario.impede.editar ?? 'Editar'}
              className="rounded-md p-[6px] text-muted hover:bg-accent/10 hover:text-accent"
            >
              <Pencil size={16} />
            </button>
          </li>
        ))}
      </ul>

      {editando !== undefined && (
        <FormularioUsuario
          key={editando?.id ?? 'novo'}
          usuario={editando}
          grupos={data.permissoes}
          souAdministrador={data.sou_administrador}
          aoFechar={() => setEditando(undefined)}
          aoMudar={() => void queryClient.invalidateQueries({ queryKey: ['usuarios'] })}
        />
      )}
    </div>
  );
}
